import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import CardHeader from '../shared/CardHeader';
import CardContainer from '../shared/CardContainer';
import { BarChart } from '@mui/x-charts';
import { dataVisColors } from '@mercedes-benz/mui5-theme';
import { useTheme } from '@mui/material';
import DirectionsBoatFilledOutlinedIcon from '@mui/icons-material/DirectionsBoatFilledOutlined';
import DirectionsCarFilledOutlined from '@mui/icons-material/DirectionsCarFilledOutlined';
import { formatNumberDE } from '../../../../../../shared/utils/formatNumber';

const BarChartPorts = ({ title = 'N/A', subtitle = 'N/A', data = [] }) => {
  const [uniquePortKeys, setUniquePortKeys] = useState([]);
  const [dataType, setDataType] = useState('Cars');
  const theme = useTheme();
  const themeType = theme.palette.mode === 'light' ? 'light' : 'dark';
  const colorScheme = dataVisColors.basic[themeType];

  const valueFormatter = (value) =>
    `${formatNumberDE(value)} ${dataType === 'Ships' ? 'voyages' : dataType.toLowerCase()}`;

  useEffect(() => {
    if (!data || data.length === 0) return;

    // Collect unique port keys (without _Cars/_Ships) where value > 0 in any month
    const portKeysSet = new Set();
    data.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'month' && item[key] > 0) {
          if (key.endsWith('_Cars')) {
            portKeysSet.add(key.replace('_Cars', ''));
          } else if (key.endsWith('_Ships')) {
            portKeysSet.add(key.replace('_Ships', ''));
          }
        }
      });
    });

    // Sort port keys: by country code, then by port name; place 'Rest' last if present
    const sorted = Array.from(portKeysSet).sort((a, b) => {
      if (a === 'Rest') return 1;
      if (b === 'Rest') return -1;

      const getCountry = (pk) =>
        pk.includes(' (') ? pk.split(' (')[1].slice(0, -1) : pk;
      const getPort = (pk) => (pk.includes(' (') ? pk.split(' (')[0] : pk);

      const countryA = getCountry(a);
      const countryB = getCountry(b);
      const portA = getPort(a);
      const portB = getPort(b);

      return countryA.localeCompare(countryB) || portA.localeCompare(portB);
    });

    setUniquePortKeys(sorted);
  }, [data]);

  const handleDataTypeChange = (event, newDataType) => {
    if (newDataType !== null) {
      setDataType(newDataType);
    }
  };

  // Process dataset: create new items with keys without suffix, values as numbers (ensure no non-numerics)
  const processedDataset = data.map((item) => {
    const newItem = { month: item.month };
    Object.keys(item).forEach((key) => {
      if (key !== 'month') {
        const value = Number(item[key]);
        if (!isNaN(value)) {
          if (dataType === 'Cars' && key.endsWith('_Cars')) {
            newItem[key.replace('_Cars', '')] = value;
          } else if (dataType === 'Ships' && key.endsWith('_Ships')) {
            newItem[key.replace('_Ships', '')] = value;
          }
        }
      }
    });
    return newItem;
  });

  return (
    <CardContainer>
      <Grid container spacing={0}>
        <Grid item xs={6}>
          <CardHeader title={title} />
        </Grid>
        <Grid item xs={6} sx={{ textAlign: 'right' }}>
          <ToggleButtonGroup
            color='primary'
            value={dataType}
            exclusive
            onChange={handleDataTypeChange}
            aria-label='Data Type'
            sx={{ marginBottom: '20px' }}
          >
            <ToggleButton value='Cars'>
              <DirectionsCarFilledOutlined />
            </ToggleButton>
            <ToggleButton value='Ships'>
              <DirectionsBoatFilledOutlinedIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      </Grid>
      <Box padding='10px'>
        <Typography variant='h6'>{subtitle}</Typography>
        {processedDataset.length > 0 && uniquePortKeys.length > 0 ? (
          <BarChart
            height={364}
            colors={colorScheme}
            dataset={processedDataset}
            xAxis={[{ scaleType: 'band', dataKey: 'month' }]}
            yAxis={[{ valueFormatter: formatNumberDE }]}
            series={uniquePortKeys.map((portKey) => {
              const isRest = portKey === 'Rest';
              const country = isRest
                ? 'Rest'
                : portKey.split(' (')[1]?.slice(0, -1) || 'Unknown';
              return {
                dataKey: portKey,
                label: portKey,
                stack: country,
                valueFormatter,
              };
            })}
          />
        ) : (
          <Typography>No data available for {subtitle}</Typography>
        )}
      </Box>
    </CardContainer>
  );
};

export default BarChartPorts;
